import { effect, EffectRef, inject, Injectable, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { BreadcrumbService } from './breadcrumb.service';

export const APP_NAME = 'Aperture Credentials Manager';

interface TitleDynamicData {
    titleKey?: string;
    titleFallback?: string;
}

/**
 * Updates `document.title` on every navigation.
 *
 * Resolution rules, evaluated against the deepest matched route:
 *
 * 1. If that route declares its own static `title` on the Route config,
 *    it wins -- e.g. a specific `edit` view can override an inherited
 *    entity-name title.
 * 2. Otherwise if `data.titleKey` is present (possibly inherited from a
 *    parent, since `paramsInheritanceStrategy: 'always'` merges data into
 *    children), resolve the label dynamically via `BreadcrumbService` and
 *    re-update the document title as producer pages push new values.
 *    Until the label arrives, `data.titleFallback` is used.
 * 3. Otherwise fall back to Angular's `buildTitle()`, which walks the
 *    snapshot for any ancestor `title`.
 * 4. Otherwise leave the document title as `APP_NAME` alone.
 *
 * All resolved titles are suffixed with ` | ${APP_NAME}` to preserve the
 * app-name context already set in `index.html`.
 *
 * A single `EffectRef` is retained across navigations; it is destroyed
 * before a new one is created, preventing stale effects from writing the
 * wrong title after the user has navigated elsewhere.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
    private readonly title = inject(Title);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly injector = inject(Injector);

    private activeEffect: EffectRef | null = null;

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.activeEffect?.destroy();
        this.activeEffect = null;

        const deepest = this.getDeepestRoute(snapshot.root);

        const ownStaticTitle = deepest.routeConfig?.title;
        if (typeof ownStaticTitle === 'string') {
            this.writeTitle(ownStaticTitle);
            return;
        }

        const dynamic = deepest.data as TitleDynamicData;
        if (dynamic.titleKey) {
            const labelSignal = this.breadcrumbService.label(dynamic.titleKey);
            const fallback = dynamic.titleFallback ?? '';
            this.activeEffect = effect(
                () => {
                    const resolved = labelSignal() ?? fallback;
                    this.writeTitle(resolved);
                },
                { injector: this.injector }
            );
            return;
        }

        const inheritedStaticTitle = this.buildTitle(snapshot);
        this.writeTitle(inheritedStaticTitle ?? '');
    }

    private writeTitle(pageTitle: string): void {
        this.title.setTitle(pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME);
    }

    private getDeepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
        let current = route;
        while (current.firstChild) {
            current = current.firstChild;
        }
        return current;
    }
}
